import { useRef, useState } from "react";
import { useUploadImage } from "../../services/images";
import assetPath from "../../utils/assetPath";

const imgUploadIcon = assetPath("/icons/upload-icon.png");

const MAX_FILE_SIZE_BYTES = 9 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ["image/png", "image/jpeg"];

interface ImageUploadProps {
  label: string;
  initialUrl?: string;
  onKeyChange: (key: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

const ImageUpload = ({ label, initialUrl, onKeyChange, onBusyChange }: ImageUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(initialUrl ?? "");
  const [validationError, setValidationError] = useState("");
  const { mutate: upload, isPending: isUploading, isError, error, reset: resetMutation } = useUploadImage();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setValidationError("Only PNG, JPG, and JPEG images are allowed");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setValidationError("Image size must not exceed 9MB.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setValidationError("");
    resetMutation();

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);
    onBusyChange?.(true);
    upload(
      { file, filename: file.name, content_type: file.type || "application/octet-stream" },
      {
        onSuccess: ({ key }) => { onKeyChange(key); onBusyChange?.(false); },
        onError: () => onBusyChange?.(false),
      }
    );
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview("");
    onKeyChange("");
    onBusyChange?.(false);
    setValidationError("");
    resetMutation();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-[12px] font-medium text-content-label">{label}</label>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full rounded-[6px] border-2 border-dashed border-[#c2c7c9] flex flex-col items-center justify-center gap-2 py-6 transition-colors hover:border-[#023a36] bg-surface-input"
      >
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Preview" className="h-[100px] object-contain rounded" />
            {isUploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded">
                <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="#023a36" strokeWidth="4" />
                  <path className="opacity-75" fill="#023a36" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleClear}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-[#c2c7c9] flex items-center justify-center shadow-sm hover:bg-red-50 transition-colors"
                aria-label="Remove image"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 10 10">
                  <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="#42484a" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <>
            <img src={imgUploadIcon} alt="" className="w-6 h-6 opacity-50" />
            <span className="text-[13px] text-content-muted">Click to upload an image</span>
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
        className="sr-only"
        onChange={handleChange}
      />
      {(validationError || isError) && (
        <span className="text-[11px] text-content-error">
          {validationError || (error as Error)?.message || "Image upload failed. Please try again."}
        </span>
      )}
    </div>
  );
};

export default ImageUpload;
